import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AvatarPreview } from "../components/AvatarEditor";
import { QuizzleController } from "./QuizzleController";
import { RaceLobby } from "./RaceLobby";
import { DrawController } from "./DrawController";
import { useClassRoom } from "../classroom/useClassRoom";
import { getStudentRecord, type ClassJoinOpts } from "../classroom/types";

/** Student class page — holds the class connection and follows the teacher.
 *  In the lobby it waits; when the teacher launches an activity it EMBEDS the
 *  game (using the class studentId as the player id, so a rejoin restores the
 *  student's score). The class connection stays alive underneath, so the
 *  teacher's Back-to-Lobby always pulls the student back here. */
export function ClassLobby() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const code = (params.get("code") ?? "").toUpperCase();
  const studentId = params.get("student") ?? "";
  const rec = useMemo(() => getStudentRecord(code, studentId), [code, studentId]);

  const joinOpts = useMemo<ClassJoinOpts | null>(
    () => (code && studentId ? { code, role: "student", studentId, name: rec?.name, avatarConfig: rec?.avatar } : null),
    [code, studentId, rec],
  );
  const { state, error } = useClassRoom(joinOpts);

  const me = state?.roster.find((s) => s.id === studentId);
  const name = me?.name ?? rec?.name ?? "You";
  const avatar = me?.avatarConfig ?? rec?.avatar ?? {};
  const mode = state?.mode ?? "lobby";

  const identity = useMemo(
    () => ({ id: studentId, displayName: name, avatarConfig: avatar }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studentId, name],
  );

  if (!code || !studentId) {
    return <div className="class-lobby"><p>Not logged in.</p><button className="btn btn-green" onClick={() => nav("/class")}>Join a class</button></div>;
  }
  if (error) {
    return <div className="class-lobby"><p className="class-err">⚠️ {error}</p><button className="btn btn-ghost" onClick={() => nav(`/class/login?code=${code}`)}>← Back</button></div>;
  }

  // Teacher launched an activity → embed it (class connection stays live).
  if (mode !== "lobby" && state?.activityCode) {
    return (
      <div className="class-activity">
        {mode === "quizzle" && (
          <QuizzleController key={state.activityCode} codeOverride={state.activityCode} identity={identity} embedded />
        )}
        {mode === "word-runner" && (
          <RaceLobby key={state.activityCode} codeOverride={state.activityCode} identity={identity} embedded asHost={false} />
        )}
        {mode === "draw-it" && (
          <DrawController key={state.activityCode} codeOverride={state.activityCode} identity={identity} embedded />
        )}
      </div>
    );
  }

  return (
    <div className="class-lobby">
      <div className="class-lobby-me">
        <span className="class-lobby-avatar"><AvatarPreview cfg={avatar} large /></span>
        <h1>{name}</h1>
        <p className="class-dim">{state?.className || "Class"} · {code} · {state?.onlineIds.length ?? 1} online</p>
      </div>
      <div className="class-lobby-status">
        <div className="class-lobby-wait" aria-hidden>⏳</div>
        <h2>Waiting for your teacher…</h2>
        <p className="class-dim">Keep this open — the teacher will start an activity.</p>
      </div>
      <button className="btn btn-ghost class-lobby-switch" onClick={() => nav(`/class/login?code=${code}`)}>Not you? Switch student</button>
    </div>
  );
}
